/**
 * client/net/prediction.ts — local player prediction + reconciliation [NET §7.3].
 * Runs the SAME shared pmove (built by createPmoveContext) as the server [NET pitfall 1].
 * OWNER: NETCODE agent.
 *
 * - onCmd: every generated cmd is kept as pending and predicted at once; its new predictable
 *   events are played immediately (src 'pred').
 * - onSnapshot (Q3 CG_PredictPlayerState): restore the server PlayerState, drop acked cmds, replay
 *   the rest. The position difference between the old and the new prediction of the same cmd is
 *   the prediction error; it is added to a visual offset that decays linearly over
 *   PRED_ERROR_DECAY_MS (Q3 cg_errordecay 100). Errors > PRED_TELEPORT_DIST, or a spawnCount change
 *   (respawn/teleport), snap instead (Q3 "PredictionTeleport") [NET pitfall 9].
 * - Events: dedupe by eventSequence so replays never play an event twice [NET pitfall 7]. An event
 *   whose seq was already played but whose content changed after reconciliation is played again
 *   (Q3 CG_CheckChangedPredictableEvents); events with a newer seq that only the server produced
 *   are played when the snapshot arrives (Q3 CG_CheckPlayerstateEvents).
 */
import { PRED_ERROR_DECAY_MS, PRED_TELEPORT_DIST } from '@shared/constants';
import { type Vec3 } from '@shared/math';
import { clonePlayerState, copyPlayerState, createPlayerState, diffPlayerState, forEachNewEvent } from '@shared/playerstate';
import { pmove } from '@shared/pmove/pmove';
import type { PmoveContext } from '@shared/pmove/types';
import { copyUserCmd, createUserCmd, MAX_PS_EVENTS, type PEv, type PlayerState, type UserCmd } from '@shared/types';

/** Pending (unacked) cmds kept at most (2 s at 60 Hz); older ones can no longer be replayed. */
export const MAX_PENDING_CMDS = 128;
/** Below this the replayed position counts as identical (a real misprediction is >> this). */
const ERROR_EPSILON = 1e-4;

export interface PredictedEvent {
  ev: PEv;
  parm: number;
  seq: number;
}

/** Desync diagnostics for one correction (shown in window.__game.session.netDebug.lastCorrections). */
export interface CorrectionInfo {
  /** Position error (units). */
  err: number;
  /** First PlayerState field that differed between the old and the new prediction of the same cmd. */
  field: string | null;
  /** Last acked cmd and number of replayed cmds. */
  ack: number;
  replayed: number;
  /** pm_type / pm_flags of the new prediction (context). */
  pmType: number;
  pmFlags: number;
}

export class Predictor {
  /** Latest predicted state and the one before it (render interpolates between them). */
  readonly predicted: PlayerState = createPlayerState();
  readonly prev: PlayerState = createPlayerState();
  private pending: UserCmd[] = [];
  private hasState = false;
  /** Prediction error offset (Q3 cg.predictedError) and when it was set. */
  readonly errorOffset: Vec3 = [0, 0, 0];
  errorTime = -1e9;
  corrections = 0;
  maxError = 0;
  /** Last error seen (diagnostics). */
  lastError = 0;
  /** Highest event sequence already played locally (16-bit, -1 = none yet). */
  private playedSeq = -1;
  /** seq -> ev | parm << 8 of recently played events, to detect changed predictions. */
  private readonly played = new Map<number, number>();
  private readonly scratch: PlayerState = createPlayerState();
  private readonly before: PlayerState = createPlayerState();
  /** Last few corrections (newest last), for desync debugging. */
  readonly corrections_log: CorrectionInfo[] = [];
  /** Number of the cmd `predicted` corresponds to (-1 = authoritative state only). */
  private predictedCmd = -1;
  /** Called once for each event the first time it is predicted, with the state right after its cmd. */
  onFreshEvent: ((ps: Readonly<PlayerState>, e: PredictedEvent, cmd: Readonly<UserCmd>) => void) | null = null;

  constructor(private readonly ctx: PmoveContext) {}

  get ready(): boolean { return this.hasState; }
  get pendingCount(): number { return this.pending.length; }

  /** Predict one new cmd; pushes the events to play now into `out`. */
  onCmd(cmd: UserCmd, out: PredictedEvent[]): void {
    if (!this.hasState) return;
    const c = copyUserCmd(createUserCmd(), cmd);
    this.pending.push(c);
    if (this.pending.length > MAX_PENDING_CMDS) this.pending.shift();
    copyPlayerState(this.prev, this.predicted);
    const seq0 = this.predicted.eventSequence;
    pmove(this.predicted, c, this.ctx);
    this.predictedCmd = c.number;
    forEachNewEvent(this.predicted, seq0, (ev, parm, seq) => {
      if (!this.isNewSeq(seq)) return;
      const e = { ev, parm, seq };
      this.markPlayed(e);
      out.push(e);
      this.onFreshEvent?.(this.predicted, e, c);
    });
  }

  /**
   * Reconcile with an authoritative PlayerState (already decoded, bit exact). `out` receives
   * server-only or changed events to play now.
   */
  onSnapshot(ps: PlayerState, lastExecutedCmd: number, realNow: number, out: PredictedEvent[] = []): void {
    let drop = 0;
    while (drop < this.pending.length && this.pending[drop].number <= lastExecutedCmd) drop++;
    if (drop) this.pending.splice(0, drop);
    const hadState = this.hasState;
    const spawnChanged = hadState && ps.spawnCount !== this.predicted.spawnCount;
    const oldX = this.predicted.origin[0], oldY = this.predicted.origin[1], oldZ = this.predicted.origin[2];
    const lastNum = this.pending.length ? this.pending[this.pending.length - 1].number : lastExecutedCmd;
    const comparable = hadState && this.predictedCmd === lastNum;
    if (comparable) copyPlayerState(this.before, this.predicted);
    const st = copyPlayerState(this.scratch, ps);
    copyPlayerState(this.prev, st);
    for (let i = 0; i < this.pending.length; i++) {
      if (i === this.pending.length - 1) copyPlayerState(this.prev, st);
      pmove(st, this.pending[i], this.ctx);
    }
    copyPlayerState(this.predicted, st);
    this.predictedCmd = lastNum;
    this.hasState = true;
    if (this.playedSeq < 0 || !hadState) {
      // first state: nothing before this point is ours to play
      this.playedSeq = (st.eventSequence - 1) & 0xffff;
      this.played.clear();
    } else this.checkEvents(st, out);
    if (!hadState || spawnChanged) { this.clearError(); return; }
    const ex = oldX - st.origin[0], ey = oldY - st.origin[1], ez = oldZ - st.origin[2];
    const len = Math.sqrt(ex * ex + ey * ey + ez * ez);
    this.lastError = len;
    if (len <= ERROR_EPSILON) return;
    this.corrections++;
    if (len > this.maxError) this.maxError = len;
    this.corrections_log.push({
      err: len, field: comparable ? diffPlayerState(this.before, st) : null, ack: lastExecutedCmd, replayed: this.pending.length,
      pmType: st.pm_type, pmFlags: st.pm_flags,
    });
    if (this.corrections_log.length > 8) this.corrections_log.shift();
    if (len > PRED_TELEPORT_DIST) { this.clearError(); return; }
    const f = this.decayFactor(realNow);
    this.errorOffset[0] = this.errorOffset[0] * f + ex;
    this.errorOffset[1] = this.errorOffset[1] * f + ey;
    this.errorOffset[2] = this.errorOffset[2] * f + ez;
    this.errorTime = realNow;
  }

  /** Decayed error offset at time t (Q3 cg_errordecay, linear over 100 ms), written into out. */
  currentError(t: number, out: Vec3 = [0, 0, 0]): Vec3 {
    const f = this.decayFactor(t);
    out[0] = this.errorOffset[0] * f; out[1] = this.errorOffset[1] * f; out[2] = this.errorOffset[2] * f;
    return out;
  }

  /** Forget everything (new match / reconnect). */
  reset(): void {
    this.pending = [];
    this.predictedCmd = -1;
    this.hasState = false;
    this.playedSeq = -1;
    this.played.clear();
    this.clearError();
  }

  /** Copy of the authoritative state replayed over pending cmds (tests). */
  clonePredicted(): PlayerState { return clonePlayerState(this.predicted); }

  private decayFactor(t: number): number {
    const f = 1 - (t - this.errorTime) / PRED_ERROR_DECAY_MS;
    return f <= 0 ? 0 : f > 1 ? 1 : f;
  }

  private clearError(): void {
    this.errorOffset[0] = this.errorOffset[1] = this.errorOffset[2] = 0;
    this.errorTime = -1e9;
  }

  private isNewSeq(seq: number): boolean {
    const d = (seq - this.playedSeq) & 0xffff;
    return d !== 0 && d < 0x8000;
  }

  private markPlayed(e: PredictedEvent): void {
    this.playedSeq = e.seq;
    this.played.set(e.seq, e.ev | (e.parm << 8));
    if (this.played.size > 64) {
      const oldest = this.played.keys().next().value as number;
      this.played.delete(oldest);
    }
  }

  private checkEvents(st: PlayerState, out: PredictedEvent[]): void {
    const end = st.eventSequence;
    const start = (end - MAX_PS_EVENTS) & 0xffff;
    for (let s = start; s !== end; s = (s + 1) & 0xffff) {
      const slot = s & (MAX_PS_EVENTS - 1);
      const ev = st.events[slot] as PEv, parm = st.eventParms[slot];
      if (this.isNewSeq(s)) {
        const e = { ev, parm, seq: s };
        this.markPlayed(e);
        out.push(e);
      } else {
        const prev = this.played.get(s);
        if (prev !== undefined && prev !== (ev | (parm << 8))) {
          this.played.set(s, ev | (parm << 8));
          out.push({ ev, parm, seq: s }); // mispredicted event: play the corrected one
        }
      }
    }
  }
}
